import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./tokenlyglobal.css";

const Login = () => {

  const [mode, setMode] = useState("login");

  const [phone, setPhone] = useState("");
  const [pin, setPin] = useState(""); // 4-digit PIN
  const [signupName, setSignupName] = useState("");
  const [signupPhone, setSignupPhone] = useState("");
  const [signupPin, setSignupPin] = useState("");
  const [signupPinConfirm, setSignupPinConfirm] = useState("");
  const [signupCity, setSignupCity] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [showSignupPin, setShowSignupPin] = useState(false);
  const [showConfirmPin, setShowConfirmPin] = useState(false);


  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // PIN input refs for auto-focus
  const pinRef = useRef(null);
  const signupPinRef = useRef(null);
  const signupPinConfirmRef = useRef(null);

  // First input refs for auto-focus
  const loginPhoneRef = useRef(null);
  const signupNameRef = useRef(null);

  const navigate = useNavigate();

  // Auto-focus first input when component mounts or mode changes
  useEffect(() => {
    console.log("useEffect triggered, mode:", mode);
    const timer = setTimeout(() => {
      if (mode === "login" && loginPhoneRef.current) {
        console.log("Focusing login phone");
        loginPhoneRef.current.focus();
      } else if (mode === "signup" && signupNameRef.current) {
        console.log("Focusing signup name");
        signupNameRef.current.focus();
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [mode]);

  const renderTitle = () => {
    if (mode === "login") return "Login to Tokenly";
    if (mode === "signup") return "Create your Tokenly account";
    return "";
  };

  const renderSubtitle = () => {
    if (mode === "login") return "Enter your phone and 4-digit PIN.";
    if (mode === "signup") return "Fill in details to get started with Tokenly.";
    return "";
  };


  const handlePinChange = (setter, value, nextRef = null) => {
    const numValue = value.replace(/[^0-9]/g, '').slice(0, 4);
    setter(numValue);

    if (nextRef && numValue.length === 4) {
      nextRef.current?.focus();
    }
  };

  // LOGIN submit handler
  const handleLogin = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");

    if (!phone || !pin || pin.length !== 4) {
      setErrorMsg("Please enter phone and 4-digit PIN.");
      return;
    }

    try {
      setLoading(true);
      // Add device info like mobile code
      const deviceInfo = {
        device_id: 'web_device_' + Date.now(),
        device_type: 'web'
      };

      const res = await fetch("https://tokenly.biswaas.com/api/user/signin", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone: phone,
          password: pin,
          ...deviceInfo
        }), // PIN as password
      });

      const json = await res.json();

      if (!json.status) {
        setErrorMsg(json.message || "Login failed. Check your PIN.");
      } else {
        setSuccessMsg(json.message || "Logged in successfully!");

        console.log("Login response:", json.data);
        const { token, phone: apiPhone, fullname, id, role_id, is_clinic, doctor_id } = json.data || {};
        if (token) localStorage.setItem("tokenly_token", token);
        localStorage.setItem(
          "tokenly_user",
          JSON.stringify({
            userId: json.data.id,
            phone: apiPhone,
            name: fullname,
            doctorId: doctor_id || id, // Prioritize doctor_id for staff
            roleId: role_id,
            isClinic: is_clinic
          })
        );

        // Role-based routing like mobile code
        if (role_id === 6) {
          // User login
          const redirectBackUrl = decodeURIComponent(
            document.cookie
              .split('; ')
              .find(row => row.startsWith('redirectBackUrl='))
              ?.split('=')[1] || ''
          );
          if (redirectBackUrl) {
            document.cookie = `redirectBackUrl=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
            navigate(redirectBackUrl);
          } else {
            navigate("/");
          }
        } else if (role_id === 3 || role_id === 4) {
          // Doctor and Staff login - redirect to doctor dashboard
          navigate("/doctor-dashboard");
        } else {
          // Other roles - redirect to home
          navigate("/");
        }
      }
    } catch (err) {
      setErrorMsg("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // SIGNUP submit handler
  const handleSignup = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");

    if (!signupName || !signupPhone || !signupPin || !signupPinConfirm || !signupCity || !termsAccepted ||
      signupPin.length !== 4 || signupPinConfirm.length !== 4) {
      setErrorMsg("Please fill all fields and accept terms & conditions.");
      return;
    }

    if (signupPin !== signupPinConfirm) {
      setErrorMsg("PINs do not match.");
      return;
    }

    try {
      setLoading(true);
      // Add device info like mobile code
      const deviceInfo = {
        device_id: 'web_device_' + Date.now(),
        device_type: 'web'
      };

      const res = await fetch("https://tokenly.biswaas.com/api/user/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fullname: signupName,
          gender: "male",
          phone_code: "+91",
          phone: signupPhone,
          password: signupPin,
          password_confirmation: signupPinConfirm,
          city: signupCity,
          role_id: 6,
          ...deviceInfo
        }),
      });

      const json = await res.json();

      if (!json.status) {
        setErrorMsg(json.message || "Signup failed.");
      } else {
        setSuccessMsg(json.message || "Account created! Please verify your mobile.");
        // Don't auto-login after signup like mobile - show success message
        setTimeout(() => {
          setMode("login");
          setSuccessMsg("");
        }, 3000);
      }
    } catch (err) {
      setErrorMsg("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="d-flex align-items-center justify-content-center py-5 min-vh-100 bg-light pt-5">
      <div className="auth-card shadow-lg rounded-4 p-4 p-md-5 w-100" style={{ maxWidth: 420, marginTop: '80px' }}>
        {/* Toggle buttons */}
        <div className="btn-group w-100 mb-4" role="group">
          <button
            type="button"
            className={`btn rounded-3 flex-fill fw-semibold ${mode === "login" ? "btn-tokenly-primary shadow-sm" : "btn-outline-secondary"
              } py-2`}
            onClick={() => {
              setMode("login");
              setErrorMsg("");
              setSuccessMsg("");
            }}
          >
            Login
          </button>
          <button
            type="button"
            className={`btn rounded-3 flex-fill fw-semibold ${mode === "signup" ? "btn-tokenly-primary shadow-sm" : "btn-outline-secondary"
              } py-2`}
            onClick={() => {
              setMode("signup");
              setErrorMsg("");
              setSuccessMsg("");
            }}
          >
            Sign Up
          </button>
        </div>

        <h3 className="fw-bold mb-2 text-center text-dark">{renderTitle()}</h3>
        <p className="text-muted mb-4 text-center small lh-sm">{renderSubtitle()}</p>

        {errorMsg && <div className="alert alert-danger py-2 mb-3 small">{errorMsg}</div>}
        {successMsg && <div className="alert alert-success py-2 mb-3 small">{successMsg}</div>}

        {/* LOGIN FORM */}
        {mode === "login" && (
          <form onSubmit={handleLogin} autoComplete="off">
            <div className="mb-3">
              <label className="form-label fw-medium mb-2" htmlFor="loginPhone">
                Phone Number
              </label>
              <input
                id="loginPhone"
                ref={loginPhoneRef}
                type="tel"
                className="form-control form-control-lg rounded-3"
                placeholder="Enter phone number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoComplete="off"
              />
            </div>

            <div className="mb-4">
              <label className="form-label fw-medium mb-2 d-flex justify-content-between align-items-center">
                <span>4-Digit PIN</span>
                {/* <small className="text-muted">(••••)</small> */}
              </label>
              <div className="pin-input-container position-relative">
                <input
                  ref={pinRef}
                  id="loginPin"
                  type={showPin ? "text" : "password"}
                  className="form-control form-control-lg rounded-3 text-center fs-4 fw-bold pe-5"
                  maxLength="4"
                  placeholder=""
                  value={pin}
                  onChange={(e) => handlePinChange(setPin, e.target.value)}
                  style={{ letterSpacing: "0.5rem" }}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="btn position-absolute top-50 end-0 translate-middle-y border-0 bg-transparent text-muted me-2"
                  onClick={() => setShowPin(!showPin)}
                  style={{ zIndex: 10 }}
                >
                  <i className={`bi ${showPin ? "bi-eye-slash" : "bi-eye"}`}></i>
                </button>
              </div>
            </div>
            <div className="text-end mt-1">
              <button
                type="button"
                className="btn btn-link p-0 small text-decoration-none"
                onClick={() => navigate("/forgot-password")}
              >
                Forgot PIN?
              </button>
            </div>

            <button
              type="submit"
              className="btn btn-tokenly-primary w-100 py-3 fw-semibold rounded-3 shadow-sm"
              disabled={loading || pin.length !== 4}
            >
              {loading ? "Logging in..." : "Login"}
            </button>
          </form>
        )}

        {/* SIGNUP FORM */}
        {mode === "signup" && (
          <form onSubmit={handleSignup} autoComplete="off">

            <div className="mb-3">
              <label className="form-label fw-medium mb-2" htmlFor="signupName">
                Full Name
              </label>
              <input
                id="signupName"
                ref={signupNameRef}
                type="text"
                className="form-control form-control-lg rounded-3"
                placeholder="Enter your full name"
                value={signupName}
                onChange={(e) => setSignupName(e.target.value)}
                autoComplete="off"
              />
            </div>

            <div className="mb-3">
              <label className="form-label fw-medium mb-2" htmlFor="signupPhone">
                Phone Number
              </label>
              <div className="input-group">
                <span className="input-group-text">+91</span>
                <input
                  id="signupPhone"
                  type="tel"
                  className="form-control form-control-lg rounded-3"
                  placeholder="Enter phone number"
                  value={signupPhone}
                  onChange={(e) => setSignupPhone(e.target.value)}
                  style={{ borderLeft: 'none' }}
                  autoComplete="off"
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label fw-medium mb-2" htmlFor="signupCity">
                City
              </label>
              <input
                id="signupCity"
                type="text"
                className="form-control form-control-lg rounded-3"
                placeholder="Enter your city"
                value={signupCity}
                onChange={(e) => setSignupCity(e.target.value)}
                autoComplete="off"
              />
            </div>


            <div className="mb-3">
              <label className="form-label fw-medium mb-2" htmlFor="signupPin">
                4-Digit PIN
              </label>
              <div className="pin-input-container position-relative">
                <input
                  ref={signupPinRef}
                  id="signupPin"
                  type={showSignupPin ? "text" : "password"}
                  className="form-control form-control-lg rounded-3 text-center fs-4 fw-bold pe-5"
                  maxLength="4"
                  placeholder=""
                  value={signupPin}
                  onChange={(e) => handlePinChange(setSignupPin, e.target.value, signupPinConfirmRef)}
                  style={{ letterSpacing: "0.5rem" }}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="btn position-absolute top-50 end-0 translate-middle-y border-0 bg-transparent text-muted me-2"
                  onClick={() => setShowSignupPin(!showSignupPin)}
                  style={{ zIndex: 10 }}
                >
                  <i className={`bi ${showSignupPin ? "bi-eye-slash" : "bi-eye"}`}></i>
                </button>
              </div>
            </div>

            <div className="mb-4">
              <label className="form-label fw-medium mb-2" htmlFor="signupPinConfirm">
                Confirm PIN
              </label>
              <div className="pin-input-container position-relative">
                <input
                  ref={signupPinConfirmRef}
                  id="signupPinConfirm"
                  type={showConfirmPin ? "text" : "password"}
                  className="form-control form-control-lg rounded-3 text-center fs-4 fw-bold pe-5"
                  maxLength="4"
                  placeholder=""
                  value={signupPinConfirm}
                  onChange={(e) => handlePinChange(setSignupPinConfirm, e.target.value)}
                  style={{ letterSpacing: "0.5rem" }}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="btn position-absolute top-50 end-0 translate-middle-y border-0 bg-transparent text-muted me-2"
                  onClick={() => setShowConfirmPin(!showConfirmPin)}
                  style={{ zIndex: 10 }}
                >
                  <i className={`bi ${showConfirmPin ? "bi-eye-slash" : "bi-eye"}`}></i>
                </button>
              </div>
            </div>

            {/* Terms and Conditions */}
            <div className="mb-4">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="termsCheck"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="termsCheck">
                  I agree to the{' '}
                  <a
                    href="https://tokenly.biswaas.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary text-decoration-none"
                  >
                    Terms & Conditions
                  </a>
                </label>
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-tokenly-primary w-100 py-3 fw-semibold rounded-3 shadow-sm mb-3"
              disabled={loading || signupPin.length !== 4 || signupPinConfirm.length !== 4}
            >
              {loading ? "Creating account..." : "Create Account"}
            </button>

            <p className="text-center small mb-2 text-muted">
              Already have account?{" "}
              <button
                type="button"
                className="btn btn-link p-0 fw-semibold"
                onClick={() => setMode("login")}
              >
                Login
              </button>
            </p>
            <p className="text-center small mb-0 text-muted">
              Are you a doctor?{" "}
              <button
                type="button"
                className="btn btn-link p-0 fw-semibold"
                onClick={() => navigate("/doctor-registration")}
              >
                Register as Doctor
              </button>
            </p>
          </form>
        )}
      </div>

      <style jsx>{`
        .pin-input-container input {
          font-family: monospace !important;
        }
        .pin-input-container input:focus {
          border-color: #007bff !important;
          box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25) !important;
        }
        /* Hide default browser eye icon */
        input::-ms-reveal,
        input::-ms-clear {
          display: none !important;
        }
        /* For Chrome/Safari password managers */
        input::-webkit-contacts-auto-fill-button,
        input::-webkit-credentials-auto-fill-button {
          visibility: hidden;
          display: none !important;
          pointer-events: none;
        }
      `}</style>
    </div>
  );
};

export default Login;
